import { FiRefreshCw } from 'react-icons/fi';

export const paraphraseDetectionData = {
  courseId: 'paraphrase-detection',
  courseType: 'listening-course',
  title: 'Paraphrase Detection',
  icon: FiRefreshCw,
  overview: {
    impact: 'Recognise rephrased information — the core skill tested in IELTS Listening',
    focus: 'Matching paraphrased audio content to question language',
    uses: 'All listening sections, especially Sections 3 and 4',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'Why Paraphrase Detection Is Key',
      content: `<p>In IELTS Listening, the correct answer is <strong>almost never</strong> expressed using the exact same words as the question. The test deliberately paraphrases — it uses different words in the audio from the words in the question to check whether you truly understand the meaning, not just match sounds.</p>
<p>This is why students who only listen for "the same words" often choose wrong answers. A student who reads <em>"The course is affordable"</em> in the question must recognise that the speaker saying <em>"It doesn't cost very much"</em> means the same thing.</p>`,
      points: [
        'IELTS is a test of understanding, not word-matching — paraphrase is the primary tool',
        'Questions and audio deliberately use different vocabulary for the same meaning',
        'Students who expect exact word matches will be misled by distractors',
        'Developing paraphrase awareness is the single biggest score booster in listening',
        'Paraphrasing occurs in ALL question types: multiple choice, gap-fill, matching, and labelling',
      ],
    },
    {
      title: 'Types of Paraphrasing',
      content: `<p>IELTS uses several distinct paraphrasing techniques. Learning to recognise each type will sharpen your ability to match what you hear to what you read.</p>`,
      table: {
        headers: ['Paraphrase Type', 'Audio Says', 'Question Says'],
        rows: [
          ['Synonym substitution', '"The film was outstanding"', '"The movie was excellent"'],
          ['Different word forms', '"They analysed the data carefully"', '"Their careful analysis of the data"'],
          ['Sentence restructuring', '"Because it rained, the event was cancelled"', '"The event was cancelled due to rain"'],
          ['General to specific', '"The fruit was damaged"', '"The apples and oranges were bruised"'],
          ['Specific to general', '"Doctors, nurses, and paramedics arrived"', '"Medical professionals arrived"'],
          ['Negation reversal', '"It\'s not uncommon"', '"It happens quite often"'],
        ],
      },
    },
    {
      title: 'Common Paraphrase Patterns',
      content: `<p>Certain paraphrase patterns appear repeatedly in IELTS. Knowing these patterns lets you anticipate how the audio will rephrase the question text.</p>`,
      points: [
        '<strong>Adjective ↔ "not" + opposite:</strong> "expensive" → "not cheap" / "difficult" → "not easy"',
        '<strong>Active ↔ passive voice:</strong> "The company built the bridge" → "The bridge was built by the company"',
        '<strong>Noun ↔ verb form:</strong> "There was a reduction in costs" → "Costs were reduced"',
        '<strong>Cause ↔ effect reframing:</strong> "Poor diet causes health problems" → "Health problems result from poor diet"',
        '<strong>Numbers rephrased:</strong> "half the students" → "50% of participants" / "a dozen" → "twelve"',
        '<strong>Time expressions:</strong> "fortnightly" → "every two weeks" / "annually" → "once a year"',
        '<strong>Formal ↔ informal:</strong> "commence" → "start" / "purchase" → "buy" / "assist" → "help"',
      ],
      warning: 'Don\'t assume the audio will use the same register as the question. Questions may use formal vocabulary while the speaker uses everyday language, or vice versa.',
    },
    {
      title: 'Practice Strategies',
      content: `<p>Paraphrase detection improves dramatically with deliberate practice. Here are proven strategies to build this skill systematically.</p>`,
      points: [
        '<strong>Synonym brainstorming:</strong> Before listening, read the question and brainstorm 2-3 synonyms for each keyword',
        '<strong>Reverse paraphrasing:</strong> After listening, identify exactly how the audio paraphrased each question — write it down',
        '<strong>Vocabulary networks:</strong> Build topic-based vocabulary maps (e.g. all ways to say "increase": rise, grow, climb, go up, surge)',
        '<strong>Cross-checking:</strong> After practice tests, compare question text to transcript text for every answer — note the paraphrase type used',
        '<strong>Collocations:</strong> Learn common word partnerships — "make a decision" = "decide", "take into account" = "consider"',
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Paraphrases',
      type: 'select',
      instructions: 'Which audio phrase is a paraphrase of the question text?',
      questions: [
        {
          text: 'Question text: "The seminar is free of charge." Which audio phrase matches?',
          options: ['"There\'s no fee for attending the seminar"', '"The seminar costs twenty pounds"', '"The seminar is fully booked"'],
          answer: '"There\'s no fee for attending the seminar"',
          explanation: '"Free of charge" is paraphrased as "no fee for attending". Both mean you don\'t have to pay.',
        },
        {
          text: 'Question text: "The research was funded by the government." Which audio phrase matches?',
          options: ['"The study received government funding"', '"The government rejected the proposal"', '"Private investors supported the work"'],
          answer: '"The study received government funding"',
          explanation: '"Funded by the government" = "received government funding". Note: "research" → "study" is also a paraphrase.',
        },
        {
          text: 'Question text: "Attendance has declined significantly." Which audio phrase matches?',
          options: ['"Far fewer people are coming now"', '"More people attend every year"', '"The numbers have stayed the same"'],
          answer: '"Far fewer people are coming now"',
          explanation: '"Declined significantly" is paraphrased as "far fewer people". Both express a large decrease.',
        },
        {
          text: 'Question text: "The deadline was extended." Which audio phrase matches?',
          options: ['"They gave us more time to submit"', '"We had to finish earlier than planned"', '"The project started ahead of schedule"'],
          answer: '"They gave us more time to submit"',
          explanation: '"Deadline was extended" = "gave us more time to submit". The meaning is identical, but the words are completely different.',
        },
      ],
    },
    {
      title: 'Identify the Paraphrase Type',
      type: 'select',
      instructions: 'What type of paraphrasing technique is being used?',
      questions: [
        {
          text: 'Question: "The course is compulsory." Audio: "All students are required to take the course."',
          options: ['Synonym substitution', 'Sentence restructuring', 'Negation reversal'],
          answer: 'Synonym substitution',
          explanation: '"Compulsory" is replaced by "required" — a direct synonym swap.',
        },
        {
          text: 'Question: "Pollution caused the fish to die." Audio: "The death of the fish was a result of pollution."',
          options: ['Sentence restructuring', 'Synonym substitution', 'General to specific'],
          answer: 'Sentence restructuring',
          explanation: 'The same information is restructured: active cause-effect → passive noun phrase with "result of".',
        },
        {
          text: 'Question: "It\'s quite common." Audio: "It\'s not unusual at all."',
          options: ['Negation reversal', 'Different word forms', 'Specific to general'],
          answer: 'Negation reversal',
          explanation: '"Quite common" → "not unusual" uses double negation to express the same positive meaning.',
        },
        {
          text: 'Question: "The workers protested." Audio: "There was a protest by the workforce."',
          options: ['Different word forms', 'Synonym substitution', 'Sentence restructuring'],
          answer: 'Different word forms',
          explanation: 'Verb "protested" → noun "protest" and "workers" → "workforce". Same root words in different grammatical forms.',
        },
      ],
    },
    {
      title: 'Find the Matching Meaning',
      type: 'select',
      instructions: 'The question uses one phrase. Which option from the audio expresses the same meaning?',
      questions: [
        {
          text: 'Question phrase: "prior to the Industrial Revolution"',
          options: ['"before factories became widespread"', '"after the economy modernised"', '"during the growth of manufacturing"'],
          answer: '"before factories became widespread"',
          explanation: '"Prior to" = "before" and "Industrial Revolution" is paraphrased as "factories became widespread".',
        },
        {
          text: 'Question phrase: "the majority of respondents"',
          options: ['"most of the people surveyed"', '"a small number of participants"', '"exactly half of those asked"'],
          answer: '"most of the people surveyed"',
          explanation: '"Majority" = "most" and "respondents" = "people surveyed". Classic synonym + word form change.',
        },
        {
          text: 'Question phrase: "the initial phase of the project"',
          options: ['"the first stage of the work"', '"the final part of the plan"', '"the middle section of the study"'],
          answer: '"the first stage of the work"',
          explanation: '"Initial" = "first", "phase" = "stage", "project" = "work". Three simultaneous synonym swaps.',
        },
        {
          text: 'Question phrase: "residents expressed dissatisfaction"',
          options: ['"local people complained"', '"homeowners were pleased"', '"visitors gave positive reviews"'],
          answer: '"local people complained"',
          explanation: '"Residents" = "local people" and "expressed dissatisfaction" = "complained". Formal → informal register shift.',
        },
      ],
    },
    {
      title: 'Rewrite as Paraphrases',
      type: 'fill-in',
      instructions: 'Write a single-word synonym that could replace the underlined word in the paraphrase.',
      questions: [
        {
          text: 'Original: "The lecture will commence at 2pm." Paraphrase: "The talk will ___ at 2pm."',
          answer: 'start',
          explanation: '"Commence" is a formal synonym for "start" or "begin". IELTS often switches between formal and informal registers.',
        },
        {
          text: 'Original: "The results were adequate." Paraphrase: "The outcomes were ___."',
          answer: 'sufficient',
          explanation: '"Adequate" and "sufficient" both mean "enough" or "satisfactory". "Results" → "outcomes" is another synonym pair.',
        },
        {
          text: 'Original: "The bridge was constructed in 1985." Paraphrase: "The bridge was ___ in 1985."',
          answer: 'built',
          explanation: '"Constructed" → "built". Both mean to create a structure, but "built" is the simpler everyday form.',
        },
      ],
    },
    {
      title: 'Generate Paraphrases',
      type: 'writing',
      instructions: 'For each sentence, write 2-3 different paraphrases using different techniques (synonym, restructuring, word form change).',
      questions: [
        {
          text: 'Original: "The new library will be completed by the end of the year."',
          example: '1. Synonym: "The new library will be finished before December." 2. Restructuring: "By year\'s end, they\'ll have finished building the new library." 3. Word form: "The completion of the new library is expected by year\'s end."',
          tip: 'Try changing at least one keyword in each version. Use the paraphrase types from the lesson: synonyms, restructuring, and word form changes.',
        },
        {
          text: 'Original: "The professor recommended that students attend the workshop because it covers essential exam techniques."',
          example: '1. Synonym: "The lecturer suggested students go to the session as it deals with key test strategies." 2. Restructuring: "Because the workshop focuses on vital exam skills, the professor advised students to participate." 3. Formal/informal: "The teacher said students should come to the workshop — it teaches important exam methods."',
          tip: 'Notice how many words can change while the meaning stays identical. This is exactly what IELTS does.',
        },
      ],
    },
  ],
  tips: [
    'Never expect to hear the exact words from the question — always listen for meaning',
    'Before the audio plays, brainstorm synonyms for keywords in each question',
    'After practice tests, compare the transcript to the questions to study how paraphrasing was used',
    'Build your vocabulary by learning words in synonym pairs, not in isolation',
  ],
  dailyPractice: [
    'Pick 5 sentences from a news article and rewrite each using 2 different paraphrase techniques',
    'Use a thesaurus to find 3 synonyms for 10 common IELTS words daily (e.g. increase, important, difficult)',
    'Listen to a podcast and pause after key sentences — rephrase what you heard in your own words',
    'Review IELTS Listening transcripts: highlight every paraphrase between audio and questions',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Paraphrase Detection for IELTS Listening: teaches students to recognise when audio content is rephrased differently from question text. Covers 6 paraphrase types (synonym, word form, restructuring, general/specific, negation reversal), common patterns, and matching strategies.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...paraphraseDetectionData,
      title: paraphraseDetectionData.title + ' (Foundations)',
      overview: { ...paraphraseDetectionData.overview, duration: '15-20 minutes' },
      sections: paraphraseDetectionData.sections.slice(0, Math.max(2, Math.ceil(paraphraseDetectionData.sections.length / 2))),
      exercises: paraphraseDetectionData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...paraphraseDetectionData,
      title: paraphraseDetectionData.title + ' (Advanced)',
      overview: { ...paraphraseDetectionData.overview, duration: '30-35 minutes' },
      sections: paraphraseDetectionData.sections,
      exercises: paraphraseDetectionData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(paraphraseDetectionData.tips || []),
        'For Band 8+: Train yourself to understand speakers at fast natural speed.',
        'Advanced tip: Practice with varied accents and complex academic content.',
      ],
    };
  }
  return paraphraseDetectionData;
}
